import React from 'react'
import { Link } from 'react-router-dom'
import { getResizedImageUrl } from '../lib/utils'

export default function CarCard({ car }) {
  const title = car.title || car.titel?.da || car.titel || `${car.Make || ''} ${car.Model || ''} ${car.Variant || ''}`.trim()
  const fuel = car.fuel || car.Propellant || car.braendstof?.da || car.braendstof || 'Ukendt'
  const transmission = car.transmission || (car.GearType === 'A' ? 'Automatgear' : (car.GearType === 'M' ? 'Manuel' : car.GearType)) || car.gear?.da || car.gear || ''
  const slug = car.slug?.da || car.slug || car.VehicleId || car.Id
  const imageUrl = car.primaryImage || car.images?.[0] || car.Pictures?.[0] || car.galleri?.[0] || 'https://assets.auxodev.dk/assets/fc45746b-58f8-47db-83c4-feb0973bc76d/placeholder.jpg'
  const isSold = car.solgt_status === true
  const isLeasingFlag = Boolean(
    car.er_leasing === true ||
    car.er_leasing === 'true' ||
    car.isLeasing === true ||
    car.is_leasing === true ||
    car.PriceType === 'LeasingPrice' ||
    car.SaleType === 'Leasing' ||
    car.Leasing === true ||
    car.Leasing === 'true'
  )
  const year = car.year || parseInt(car.Year || car.aargang || 0, 10)
  const mileage = car.mileage ?? parseInt(car.Mileage || car.kilometer || 0, 10)

  // Priser: Både kontantpris og leasingpris understøttes
  const rawCash = parseInt(car.kontantpris || car.CashPrice || 0, 10)
  const rawLeasing = parseInt(car.leasingpris || car.LeasingPrice || car.LeasingMonthlyPayment || 0, 10)
  const fallbackPrice = parseInt(car.pris || car.price || car.Price || 0, 10)

  let cashPrice = 0
  let leasingPrice = 0

  if (rawCash > 0 && rawLeasing > 0) {
    cashPrice = rawCash
    leasingPrice = rawLeasing
  } else if (rawLeasing > 0) {
    leasingPrice = rawLeasing
    cashPrice = rawCash > 0 ? rawCash : (fallbackPrice > 0 && fallbackPrice !== rawLeasing ? fallbackPrice : 0)
  } else if (rawCash > 0) {
    cashPrice = rawCash
  } else if (fallbackPrice > 0) {
    if (isLeasingFlag) {
      leasingPrice = fallbackPrice
    } else {
      cashPrice = fallbackPrice
    }
  }

  const hasBoth = cashPrice > 0 && leasingPrice > 0
  const range = car.Range || car.range || car.raekkevidde

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-stone-200/90 bg-white transition-all duration-300 hover:border-stone-300 hover:shadow-xl shadow-sm">
      {/* Image Container */}
      <Link to={`/biler-til-salg/${slug}`} className="relative aspect-video w-full overflow-hidden bg-stone-100 block">
        <img
          src={getResizedImageUrl(imageUrl, 800)}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {isSold ? (
            <span className="rounded-md bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
              Solgt
            </span>
          ) : (
            <span className="rounded-md bg-[#DFB587] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-950 shadow-sm">
              Til salg
            </span>
          )}
          {hasBoth && !isSold && (
            <span className="rounded-md bg-neutral-900/90 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#DFB587] border border-[#DFB587]/30 shadow-sm">
              Køb & Leasing
            </span>
          )}
          {!hasBoth && leasingPrice > 0 && !isSold && (
            <span className="rounded-md bg-neutral-900/90 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white border border-stone-700 shadow-sm">
              Leasing
            </span>
          )}
          {year >= 2024 && !isSold && (
            <span className="rounded-md bg-emerald-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm">
              Nyhed
            </span>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex-1">
          <Link to={`/biler-til-salg/${slug}`} className="block">
            <h3 className="font-display text-base font-semibold text-neutral-900 line-clamp-1 group-hover:text-[#9b784a] transition-colors">
              {title}
            </h3>
          </Link>
          
          {/* Specs Grid */}
          <div className="mt-4 grid grid-cols-2 gap-y-2 gap-x-4 border-t border-stone-100 pt-4 text-xs text-neutral-600">
            <div className="flex items-center gap-1.5">
              <i className="fa-regular fa-calendar text-[#9b784a] w-4" />
              <span>Modelår: {year}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fa-regular fa-gauge text-[#9b784a] w-4" />
              <span className="whitespace-nowrap">Km: {mileage?.toLocaleString('da-DK')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <i className="fa-regular fa-gas-pump text-[#9b784a] w-4" />
              <span className="truncate">{fuel}</span>
            </div>
            {range ? (
              <div className="flex items-center gap-1.5 text-emerald-700 font-medium">
                <i className="fa-solid fa-bolt text-[#9b784a] w-4" />
                <span>{range} km rækkevidde</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <i className="fa-regular fa-gears text-[#9b784a] w-4" />
                <span className="truncate">{transmission || 'Manuel'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Price & CTA */}
        <div className="mt-5 flex items-end justify-between border-t border-stone-100 pt-4">
          <div className="min-w-0 pr-2">
            {hasBoth ? (
              <div className="flex flex-col gap-0.5">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold block leading-tight">
                    Kontant
                  </span>
                  <span className="font-display text-base font-bold text-neutral-900 leading-tight">
                    {cashPrice.toLocaleString('da-DK')} kr.
                  </span>
                </div>
                <div className="flex items-baseline gap-1 text-[#9b784a] pt-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-semibold">
                    Leasing:
                  </span>
                  <span className="font-display text-xs font-bold">
                    {leasingPrice.toLocaleString('da-DK')} kr./md.
                  </span>
                </div>
              </div>
            ) : leasingPrice > 0 ? (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">
                  Leasingpris
                </p>
                <p className="font-display text-lg font-bold text-neutral-900">
                  {leasingPrice.toLocaleString('da-DK')} kr.<span className="text-xs font-normal text-neutral-500">/md.</span>
                </p>
              </div>
            ) : (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">
                  Kontantpris
                </p>
                <p className="font-display text-lg font-bold text-neutral-900">
                  {cashPrice ? `${cashPrice.toLocaleString('da-DK')} kr.` : 'Kontakt for pris'}
                </p>
              </div>
            )}
          </div>
          
          <Link
            to={`/biler-til-salg/${slug}`}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-neutral-900 border border-neutral-900 px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#DFB587] hover:text-neutral-950 hover:border-[#DFB587] transition-all duration-300"
          >
            Se detaljer <i className="fa-solid fa-arrow-right text-[10px]" />
          </Link>
        </div>
      </div>
    </div>
  )
}
